import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { canManageCrmRoles, isSuperAdmin, isHod } from '../../utils/crmPermissions';
import { Plus, Edit, Copy, Shield } from 'lucide-react';

export default function CrmRoles() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canWrite = isSuperAdmin(user) || isHod(user) || canManageCrmRoles(user);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(searchParams.get('department_id') || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ role_name: '', description: '', department_id: '' });

  const loadDepartments = async () => {
    try {
      const res = await api.get('/crm/departments');
      const list = res.data?.data?.items || [];
      setDepartments(list);
      if (!departmentId && list.length) {
        const first = String(list[0].id);
        setDepartmentId(first);
        setSearchParams({ department_id: first });
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load departments', variant: 'destructive' });
    }
  };

  const loadRoles = async (deptId) => {
    if (!deptId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/crm/roles', { params: { department_id: deptId } });
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load roles', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDepartments(); }, []);
  useEffect(() => {
    if (departmentId) loadRoles(departmentId);
  }, [departmentId]);

  const onDeptChange = (v) => {
    setDepartmentId(v);
    setSearchParams({ department_id: v });
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ role_name: '', description: '', department_id: departmentId });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      role_name: row.role_name || '',
      description: row.description || '',
      department_id: String(row.department_id),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        role_name: form.role_name,
        description: form.description,
        department_id: Number(form.department_id || departmentId),
      };
      if (editing) {
        await api.put(`/crm/roles/${editing.id}`, payload);
        toast({ title: 'Role updated' });
      } else {
        await api.post('/crm/roles', payload);
        toast({ title: 'Role created' });
      }
      setOpen(false);
      loadRoles(departmentId);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
    }
  };

  const duplicate = async (row) => {
    try {
      await api.post(`/crm/roles/${row.id}/duplicate`);
      toast({ title: 'Role duplicated' });
      loadRoles(departmentId);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Duplicate failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl font-semibold text-foreground">Roles</h1>
        {canWrite && (
          <Button onClick={openCreate} size="sm" disabled={!departmentId}>
            <Plus className="w-4 h-4 mr-1" /> Add Role
          </Button>
        )}
      </div>

      <div className="max-w-sm">
        <Label>Department</Label>
        <Select value={departmentId || undefined} onValueChange={onDeptChange}>
          <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
          <SelectContent>
            {departments.map((d) => (
              <SelectItem key={d.id} value={String(d.id)}>{d.department_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Department Roles</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && items.length === 0 && <p className="text-sm text-muted-foreground">No roles in this department.</p>}
          {items.map((row) => (
            <div key={row.id} className="flex items-center justify-between gap-2 border-b border-border py-3 last:border-0">
              <div>
                <div className="font-medium text-foreground">{row.role_name}</div>
                <div className="text-xs text-muted-foreground">
                  {row.status}{row.is_system_role ? ' · system' : ''}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/crm/roles/${row.id}/permissions`}><Shield className="w-4 h-4 mr-1" /> Permissions</Link>
                </Button>
                {canWrite && (
                  <>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(row)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => duplicate(row)}><Copy className="w-4 h-4" /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Role' : 'New Role'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.role_name} onChange={(e) => setForm({ ...form, role_name: e.target.value })} /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
